import _ from 'underscore';
import classNames from 'classnames';
import React, { useCallback, useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Text,
  Tooltip,
} from '@fluentui/react-components';

import { DropdownTreeSelect } from '@bingads-webui-campaign-react/dropdown-tree-select';

import { useCampaignData, getAdGroupData } from './adgroup-selector-data';

interface ICampaignAdgroupSelectorProps {
  defalutSelectedCampaign?: {
    Id: string;
    Name: string;
  };
  defalutSelectedAdGroup?: {
    Id: string;
    Name: string;
  };
  showSelectedTag?: boolean;
  isAdGroupLevel: boolean;
  onSelectionChange: ({
    selectedCampaign,
    selectedAdGroup,
  }: {
    selectedCampaign: { Id: string, Name: string },
    selectedAdGroup?: { Id: string, Name: string },
  }) => void;
  onInitFinished: () => void;
  jsdata: any;
  cid: number,
  aid: number,
  campaignId?: number,
}

const useStyles = makeStyles({
  selectedTag: {
    display: 'inline-block',
    maxWidth: '100%',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    backgroundColor: tokens.colorNeutralBackground1Selected,
    ...shorthands.overflow('hidden'),
    ...shorthands.borderRadius('3px'),
    ...shorthands.gap('6px'),
    ...shorthands.padding('6px', '12px'),
    ...shorthands.margin('0px', '0px', '6px'),
  },
});

export function CampaignAdgroupSelector({
  defalutSelectedCampaign,
  defalutSelectedAdGroup,
  isAdGroupLevel,
  showSelectedTag,
  onInitFinished,
  onSelectionChange,
  jsdata,
  cid,
  aid,
  campaignId,
}: ICampaignAdgroupSelectorProps) {
  const classes = useStyles();
  const {
    data: items,
    isLoading,
  } = useCampaignData({ jsdata, isAdGroupLevel, campaignId });

  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [openItems, setOpenItems] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(defalutSelectedCampaign);
  const [selectedAdGroup, setSelectedAdGroup] = useState(defalutSelectedAdGroup);

  useEffect(() => {
    if (!isLoading) {
      onInitFinished();
    }
  }, [isLoading, onInitFinished]);

  const onChange = useCallback(([{ value, content, parentValue }]) => {
    setCheckedItems([value]);
    if (isAdGroupLevel) {
      const adGroup = {
        Id: value,
        Name: content,
      };
      setSelectedAdGroup(adGroup);
      const campaignFromItems = _.find(items, (item : {
        value: string,
        content: string,
      }) => item.value === parentValue);
      const campaign = {
        Id: parentValue,
        Name: campaignFromItems?.content,
      };
      setSelectedCampaign(campaign);
      onSelectionChange({
        selectedCampaign: campaign,
        selectedAdGroup: adGroup,
      });
    } else {
      const campaign = {
        Id: value,
        Name: content,
      };
      setSelectedCampaign(campaign);
      onSelectionChange({
        selectedCampaign: campaign,
      });
    }
  }, [isAdGroupLevel, items, onSelectionChange]);

  const onOpenChange = useCallback((allOpenItems) => {
    setOpenItems(allOpenItems);
  }, []);
  const lazyLoad = useCallback(parentValue => getAdGroupData({
    query: '',
    jsdata,
    cid,
    aid,
    campaignId: parentValue,
  }), [aid, cid, jsdata]);

  const selectedEntityName = isAdGroupLevel ? selectedAdGroup?.Name : selectedCampaign?.Name;

  return (
    <div className="ba-fluent-campaign-adgroup-selector">
      { showSelectedTag &&
        <Tooltip content={selectedEntityName} relationship="label">
          <Text className={classNames('selected-tag', classes.selectedTag)}>{selectedEntityName}</Text>
        </Tooltip>
      }
      { !isLoading &&
        <DropdownTreeSelect
          placeholder={selectedEntityName}
          items={items}
          checkedItems={checkedItems}
          onChange={onChange}
          openItems={openItems}
          onOpenChange={onOpenChange}
          lazyLoad={isAdGroupLevel ? lazyLoad : undefined}
          isMultiSelect={false}
        />
      }
    </div>
  );
}

CampaignAdgroupSelector.defaultProps = {
  defalutSelectedCampaign: {},
  defalutSelectedAdGroup: {},
  showSelectedTag: true,
  campaignId: null,
};
